import { Lock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { tierLabel, upgradeSheetContent, type GatedFeature, type PlanTier } from '@/lib/featureGate';
import { cn } from '@/lib/utils';

/** Small lock + "Starter"/"Growth" badge for a locked control. */
export function PlanLockBadge({ plan, className }: { plan: PlanTier | null; className?: string }) {
  const label = tierLabel(plan);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10.5px] font-semibold text-amber-900 dark:bg-amber-500/15 dark:text-amber-300',
        className,
      )}
      data-testid="badge-plan-lock"
    >
      <Lock className="h-3 w-3" aria-hidden="true" />
      {label ?? 'Upgrade'}
    </span>
  );
}

/**
 * The one upgrade sheet for plan-gated features. Rendered by
 * UpgradePlanProvider; open it through useFeatureGate().openUpgrade.
 */
export function UpgradePlanSheet({
  open,
  onOpenChange,
  feature,
  title,
  message,
  onUpgrade,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature?: GatedFeature;
  title: string;
  message?: string;
  onUpgrade: () => void;
}) {
  // No explanation, no sheet: never show the buttons on a blank panel.
  const content = upgradeSheetContent({ feature, title, message });
  if (!content) return null;
  const { body, plans } = content;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-auto max-h-[90vh] rounded-t-2xl" data-testid="sheet-upgrade-plan">
        <SheetHeader className="text-left">
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
          </div>
          <SheetTitle data-testid="text-upgrade-plan-title">{content.title}</SheetTitle>
          <SheetDescription data-testid="text-upgrade-plan-body">{body}</SheetDescription>
        </SheetHeader>
        {plans.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {plans.map((p) => (
              <span key={p} className="rounded-full border border-border px-2.5 py-1 text-[12px] font-medium">{p}</span>
            ))}
          </div>
        )}
        <Button className="mt-4 h-11 w-full rounded-xl bg-brand-lime text-brand-ink hover:bg-brand-lime-hover" onClick={onUpgrade} data-testid="button-upgrade-plan">
          Upgrade plan
        </Button>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="mt-1 min-h-[44px] w-full text-[13px] text-muted-foreground hover:text-foreground"
          data-testid="button-upgrade-plan-dismiss"
        >
          Not now
        </button>
      </SheetContent>
    </Sheet>
  );
}
